// ─────────────────────────────────────────────────────────────────────────────
// Error Logging via Sentry
//
// SETUP:
// 1. Go to https://sentry.io → create a React project
// 2. Copy your DSN → add to Vercel as REACT_APP_SENTRY_DSN
// 3. That's it — errors are automatically captured
// ─────────────────────────────────────────────────────────────────────────────

import * as Sentry from "@sentry/react";

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const IS_CONFIGURED = !!DSN;

export const initErrorLogging = () => {
  if (!IS_CONFIGURED) {
    console.log("ℹ️ Sentry not configured — error logging disabled");
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment:      process.env.NODE_ENV,
    release:          "propdesk@1.0.0",
    tracesSampleRate: 0.2, // Track 20% of transactions for performance

    // Don't log errors in development
    enabled: process.env.NODE_ENV === "production",

    // Ignore common non-critical errors
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Network request failed",
      "Load failed",
    ],

    beforeSend(event) {
      // Strip any sensitive data before sending to Sentry
      if (event.request?.cookies) delete event.request.cookies;
      if (event.user?.email)      delete event.user.email;
      return event;
    },
  });
};

// Log a specific error manually
export const logError = (error, context = {}) => {
  if (!IS_CONFIGURED) {
    console.error("Error:", error, context);
    return;
  }
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, val]) => scope.setExtra(key, val));
    Sentry.captureException(error);
  });
};

// Set user context (call after login)
export const setLoggedInUser = (user) => {
  if (!IS_CONFIGURED) return;
  Sentry.setUser({
    id:   user.id,
    role: user.role,
    // deliberately not sending email for privacy
  });
};

// Clear user context (call after logout)
export const clearLoggedInUser = () => {
  if (!IS_CONFIGURED) return;
  Sentry.setUser(null);
};
